<template>
    <div v-if="loaded">
        <rkm-form
            ref="Form"
            class="mb-2"
        >
            <rkm-row>
                <rkm-col>
                    <rkm-form-multiselect
                        :load-all-automatically="false"
                        :options="form.exames"
                        :select-limit="null"
                        label="Exame"
                        option-name-key="descricao"
                        option-text-selecteds="exame"
                        v-model="model.exames"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-multiselect
                        :load-all-automatically="false"
                        :options="form.procedimentosCirurgicos"
                        :select-limit="null"
                        label="Procedimento cirúrgico"
                        option-name-key="descricao"
                        option-text-selecteds="procedimento cirúrgico"
                        v-model="model.procedimentosCirurgicos"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-multiselect
                        :load-all-automatically="false"
                        :options="form.especialidades"
                        :select-limit="null"
                        label="Especialidade"
                        option-name-key="descricao"
                        option-text-selecteds="especialidade"
                        v-model="model.especialidades"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        @click="validateAndSearch()"
                        color="primary"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetFilters"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>
            </template>

            <rkm-data-table
                :disable-search="!camposObrigatorioPreenchidos"
                :fields="fields"
                :repository="repositories.fila.solicitacaoEspera"
                :request-params="camelObjectKeysToSnakeRecursive({...filters, customer: this.customer})"
                :tbody-tr-class="rowClass"
                class="fila"
                ref="DataTable"
                sort-by="posicao"
            >
                <template v-slot:cell(itens)="{ value }">
                    <div
                        v-for="item in value"
                        :key="item"
                    >
                        {{ item }}
                    </div>
                </template>

                <template v-slot:cell(tempoMedioPrevistoAtendimento)="{ value }">
                    <div
                        v-for="item in value"
                        :key="item"
                    >
                        {{ item }}
                    </div>
                </template>

                <template #afterTable>
                    <div class="my-2">
                        <p class="m-0">A linha em <strong class="purple">roxo</strong> representa que o paciente está em óbito.</p>
                        <p class="m-0">A linha em <strong class="yellow">amarela</strong> representa que a solicitação foi devolvida.</p>
                    </div>
                </template>
            </rkm-data-table>
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { repository, error, stringCase, object } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Enums.
import statusTipo from '@/enums/regulacao/statusTipo'
import parametrizacaoDivulgacaoColuna from '@/enums/regulacao/parametrizacaoDivulgacaoColuna'

export default {
    computed: {
        filters() {

            return this.camelObjectKeysToSnakeRecursive({
                exames: this.model.exames,
                procedimentosCirurgicos: this.model.procedimentosCirurgicos,
                especialidades: this.model.especialidades,
            })
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        columns() {

            const colunas = [
                { key: 'paciente', name: 'paciente', label: 'Paciente', visible: false, searchable: true, fixed: true },
            ]

            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.PACIENTE)) {
                colunas.push({ key: 'pacienteNome', name: 'pacienteNome', label: 'Paciente', sortable: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.CNS)) {
                colunas.push({ key: 'pacienteCns', name: 'pacienteCns', label: 'CNS', sortable: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.CPF)) {
                colunas.push({ key: 'pacienteCpf', name: 'pacienteCpf', label: 'CPF', sortable: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.CLASSIFICACAO)) {
                colunas.push({ key: 'posicao', name: 'posicao', label: 'Classificação', sortable: true, fixed: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.DATA_DA_SOLICITACAO)) {
                colunas.push({ key: 'data', name: 'data', label: 'Data da solicitação', sortable: true, fixed: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.PROCEDIMENTO_CONSULTA)) {
                colunas.push({ key: 'itens', label: 'Procedimento(s) / Especialidade', fixed: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.PRIORIDADE)) {
                colunas.push({ key: 'prioridadeNome', name: 'prioridadeNome', label: 'Prioridade', sortable: true, fixed: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.TIPO_DE_SOLICITACAO)) {
                colunas.push({ key: 'tipoSolicitacao', name: 'tipoSolicitacao', label: 'Tipo de solicitação', sortable: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.TEMPO_MEDIO_PREVISTO_PARA_ATENDIMENTO)) {
                colunas.push({ key: 'tempoMedioPrevistoAtendimento', name: 'tempoMedioPrevistoAtendimento', label: 'Tempo médio previsto para atendimento' })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.TEMPO_DE_ESPERA)) {
                colunas.push({ key: 'tempoEspera', name: 'tempoEspera', label: 'Tempo de espera', sortable: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.DATA_DA_MOVIMENTACAO)) {
                colunas.push({ key: 'dataMovimentacao', name: 'dataMovimentacao', label: 'Data da movimentação', sortable: true })
            }
            if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.OBSERVACAO)) {
                colunas.push({ key: 'observacao', name: 'observacao', label: 'Observação', sortable: true })
            }

            return colunas
        },
        camposObrigatorioPreenchidos() {
            const exames = this.model.exames?.length ?? 0
            const procedimentos = this.model.procedimentosCirurgicos?.length ?? 0
            const especialidades = this.model.especialidades?.length ?? 0

            return exames > 0 || procedimentos > 0 || especialidades > 0
        },
    },
    created() {

        this.repositories = {
            fila: this.getConectaSusRepository('regulacaoDivulgacao.fila'),
            cliente: this.getConectaSusRepository('intranet.cliente'),
        }

        this.list()
    },
    data: () => ({
        selectedAll: false,
        indeterminate: true,
        selectedCols: [],
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        enums: { statusTipo, parametrizacaoDivulgacaoColuna },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        validateAndSearch() {
            const exames = this.model.exames?.length ?? 0
            const procedimentos = this.model.procedimentosCirurgicos?.length ?? 0
            const especialidades = this.model.especialidades?.length ?? 0

            if (exames === 0 && procedimentos === 0 && especialidades === 0) {
                this.showAlert('Selecione pelo menos um dos campos: Exame, Procedimento Cirúrgico ou Especialidade.')

                return
            }

            this.$refs.Form.validate(this.$refs.DataTable.search)
        },
        list() {
            this.setLoading()

            this.repositories.fila.list({ customer: this.customer, aba: 'solicitacao-espera' })
                .then(({ data }) => {
                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.selectedCols = []

                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.PACIENTE)) {
                        this.selectedCols.push('pacienteNome')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.CNS)) {
                        this.selectedCols.push('pacienteCns')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.CPF)) {
                        this.selectedCols.push('pacienteCpf')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.TIPO_DE_SOLICITACAO)) {
                        this.selectedCols.push('tipoSolicitacao')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.TEMPO_MEDIO_PREVISTO_PARA_ATENDIMENTO)) {
                        this.selectedCols.push('tempoMedioPrevistoAtendimento')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.TEMPO_DE_ESPERA)) {
                        this.selectedCols.push('tempoEspera')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.DATA_DA_MOVIMENTACAO)) {
                        this.selectedCols.push('dataMovimentacao')
                    }
                    if (this.additionals.colunasVisiveis.includes(this.enums.parametrizacaoDivulgacaoColuna.OBSERVACAO)) {
                        this.selectedCols.push('observacao')
                    }

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        resetFilters() {
            this.$nextTick().then(() => this.list())
        },
        rowClass(item, type) {
            if (type === 'row' && item) {

                if (item.pacienteEmObito) {

                    return 'table-purple'
                }
                else {

                    switch (item.statusTipoId) {

                        case this.enums.statusTipo.DEVOLVIDO: {

                            return 'table-warning'
                        }
                        default: {

                            return ''
                        }
                    }
                }
            }
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [repository, error, stringCase, object],
    props: {
        customer: { type: String, required: true },
    },
    watch: {
        model: {
            deep: true,
            handler() {

                this.$nextTick().then(() => {
                    this.$refs.DataTable.clear()
                    this.$nextTick().then(() => this.$refs.DataTable.clearTable())
                })
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.fila >>> .purple {
    color: #733380;
}

.fila >>> .yellow {
    color: #eca20c;
}

.fila >>> .table-purple {
    border-color: #a275a9;
    background-color: #e8dbe9;
}

.fila >>> .table-orange {
    border-color: #ffb067;
    background-color: #ffd7b2;
}

.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
</style>